import { useEffect } from 'react';

import { Outlet } from 'react-router';

import { useSetAtom } from 'jotai';

import { useResetViewStateOnSectionLeave } from '@/hooks/useResetViewStateOnSectionLeave';

import { getStepResource } from '@/utils/contentStep';
import { cn } from '@/utils/tailwind';

import DocumentHeader from './Header/DocumentHeader';

import {
  clearDocumentAtom,
  setDocumentAtom,
  useGameFullscreen,
} from '@/atoms/document';
import {
  isPlanResourceReference,
  mapReferenceToDocument,
} from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/ResourceName/utils';
import { useDocProxyContext } from '@/routes/TrainingPlans/hooks/useDocProxyContext';

export default function DocumentLayout() {
  useResetViewStateOnSectionLeave('/document');
  const [isGameFullscreen] = useGameFullscreen();
  const setDocument = useSetAtom(setDocumentAtom);
  const clearDocument = useSetAtom(clearDocumentAtom);

  const { resourceReference, planName, planId, isTrainTogetherMode, step } =
    useDocProxyContext();

  // Plan Tridion steps never call useDocument, but print tracking (and some
  // header state) read documentAtom. Sync only while a plan Tridion step is
  // open so DocumentHeader / TridionTitle / Layout beforeprint share one id.
  //
  // Ownership: /document/:id still owns the atom via useDocument. This effect
  // no-ops when useDocProxyContext has no plan resource (same DocumentLayout
  // wraps both routes). We do not steal or replace useDocument on that path.
  //
  // Cleanup: clearDocument on leave wipes the plan write. Returning to
  // /document/:id remounts useDocument, which rehydrates the atom — Path B
  // must not rely on values surviving a plan visit.
  //
  // Id: step.resource.id (same as ResourceView resourceId), not reference.id.
  //
  // Deps: key on resource identity only. React Query refetches allocate a new
  // resourceReference for the same step; depending on that object would
  // clear+reset mid-view and drop in-flight resource_print.
  const resourceId = getStepResource(step)?.id;
  const resourceType = getStepResource(step)?.type;
  const sourceSystem = resourceReference?.sourceSystem;

  useEffect(() => {
    if (
      !resourceId ||
      !resourceReference ||
      !isPlanResourceReference(resourceReference) ||
      sourceSystem !== 'TRIDION'
    ) {
      return;
    }

    setDocument({
      document: {
        ...mapReferenceToDocument(resourceReference, resourceType),
        id: resourceId,
      },
    });

    return () => {
      clearDocument();
    };
    // resourceReference is read from the latest render when identity deps
    // change; omit object identity so plan refetches do not clear the atom.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [resourceId, resourceType, sourceSystem, setDocument, clearDocument]);

  return (
    <>
      <DocumentHeader
        name={resourceReference?.name}
        planName={planName}
        planId={planId}
        isTrainTogetherMode={isTrainTogetherMode}
        isTridion={resourceReference?.sourceSystem === 'TRIDION'}
      />
      <div
        className={cn(
          'md:mt-header-height relative mx-auto flex w-full justify-center pt-4 md:px-4',
          // Web (and native with NavbarMobile): subtract sticky top chrome so
          // absolute LoadingOverlay centers in the remaining viewport.
          window.native.isNative && !window.native.showNavbar
            ? 'min-h-dvh md:min-h-[calc(100dvh-var(--header-height))]'
            : 'min-h-[calc(100dvh-var(--header-height))]',
          {
            'pt-0': isGameFullscreen,
          },
        )}
      >
        <Outlet />
      </div>
    </>
  );
}
